import { Link } from "react-router-dom";
import { ClipboardList, Stethoscope, FileCheck2, ArrowRight } from "lucide-react";
import { useSeo } from "../lib/seo";
import { useAuth } from "../context/AuthContext";
import { PublicHeader, PublicFooter } from "../components/PublicShell";
import { VideoPlayer, TUTORIAL_VIDEO, FEATURES_VIDEO, WALKTHROUGH_VIDEO, videoLd } from "../components/PromoVideo";

const STEPS = [
  { Icon: ClipboardList, t: "Enter the medical profile", d: "Add the patient's conditions and medications (scientific names supported) and weight for anaesthetic dosing." },
  { Icon: Stethoscope, t: "Choose the procedure", d: "Answer the short follow-up questions or enter lab results — the decision appears only when the information is complete." },
  { Icon: FileCheck2, t: "Follow the decision", d: "Proceed, proceed with precautions, specialist approval first, or defer — with emergency/routine steps and a referral PDF." },
];

function TutorialBody({ inApp }) {
  const { user } = useAuth();
  return (
    <div data-testid="tutorial-page">
      <h1 className={`font-heading font-bold ${inApp ? "text-2xl sm:text-3xl text-slate-900" : "text-4xl sm:text-5xl text-[#0B2545]"}`}>How to use DentiCision</h1>
      <p className="text-slate-500 mt-2">A 2-minute walkthrough — from the patient's medical history to a clear, referenced decision.</p>
      <h2 className="font-heading text-lg font-semibold text-[#0B2545] mt-8">1. Getting started</h2>
      <VideoPlayer video={TUTORIAL_VIDEO} testid="tutorial-video" label="Play the DentiCision tutorial" className="mt-3" />
      <div className="grid md:grid-cols-3 gap-4 mt-8">
        {STEPS.map(({ Icon, t, d }, i) => (
          <div key={t} className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="flex items-center gap-2 font-semibold text-[#0B2545]"><Icon className="w-4 h-4 text-[#1C6FE3]" /> {i + 1}. {t}</p>
            <p className="text-sm text-slate-600 mt-1.5">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-12" data-testid="features-video-section">
        <h2 className="font-heading text-lg font-semibold text-[#0B2545]">2. QR consultations &amp; finding your patients' cases</h2>
        <p className="text-slate-500 mt-1 text-sm">Scan the QR on the lab request to reopen a case when the patient returns, search by name, mobile, file or case number, and receive specialist replies directly in DentiCision.</p>
        <VideoPlayer video={FEATURES_VIDEO} testid="features-video" label="Play the QR consultations video" className="mt-3" />
      </div>
      <div className="mt-12" data-testid="walkthrough-video-section">
        <h2 className="font-heading text-lg font-semibold text-[#0B2545]">3. Advanced features &amp; platform walkthrough</h2>
        <p className="text-slate-500 mt-1 text-sm">Prescribing safety alerts and safe alternatives, allergies, references, support and suggestions — a full tour of the platform.</p>
        <VideoPlayer video={WALKTHROUGH_VIDEO} testid="walkthrough-video" label="Play the advanced features walkthrough" className="mt-3" />
      </div>
      <Link to={user ? "/app/new" : "/register"} data-testid="tutorial-cta"
        className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-[#1C6FE3] text-white font-semibold hover:bg-[#1659b8] transition-colors">
        {user ? "Start a new evaluation" : "Start free trial"} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

export function AppTutorial() {
  return <div className="max-w-4xl"><TutorialBody inApp /></div>;
}

export default function Tutorial() {
  useSeo({ title: "How to use DentiCision — video tutorial", path: "/tutorial",
    description: "Watch the DentiCision video tutorials: enter the medical profile, follow the evidence-based decision, reopen cases with a QR code and get specialist replies online.",
    jsonLd: { "@context": "https://schema.org", "@graph": [videoLd("How to use DentiCision — video tutorial",
      "Step-by-step tutorial: enter the patient's medical profile, choose the dental procedure and follow the evidence-based DentiCision decision.", TUTORIAL_VIDEO, "PT1M44S"),
      videoLd("DentiCision — QR code consultations & smarter patient management",
      "Scan the QR on the lab request to reopen a patient's case, search cases by name, mobile, file or case number, and receive specialist replies online.", FEATURES_VIDEO),
      videoLd("DentiCision | Advanced Features & Platform Walkthrough",
      "Tour of DentiCision: prescribing safety alerts with safe alternatives, allergy checks, evidence references, support and suggestions.", WALKTHROUGH_VIDEO)] } });
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-14"><TutorialBody /></main>
      <PublicFooter />
    </div>
  );
}
